import { evalFormula } from './formulas';
import {
    ignoreGroupForLine,
    ignoreGroupForParent,
    isGroupIgnoredAtAnyLocation,
} from './ignoreGroups';
import type { CaseInputs, SchemaEntry, View } from './types';
import { LOCATION_VIEWS } from './types';

export const GPS_KEY = 'is.gross_product_sales';
export const ORDERS_KEY = 'is.total_orders';
export const AVG_ORDERS_KEY = 'is.avg_orders_per_day';
export const AVG_TICKET_KEY = 'is.avg_ticket';
export const STORE_STATUS_KEY = 'is.store_status';

export const LABOR_SUM_KEYS = new Set([
    'is.hourly_fte_boh',
    'is.hourly_fte_foh',
    'is.salaried_headcount',
]);
export const LABOR_RATE_KEYS = new Set([
    'is.avg_hourly_rate_boh',
    'is.avg_hourly_rate_foh',
    'is.avg_salary_annual',
]);
const LABOR_INPUT_KEYS = new Set([...LABOR_SUM_KEYS, ...LABOR_RATE_KEYS]);

const EXCLUDED_FROM_SUM = new Set([
    AVG_TICKET_KEY,
    AVG_ORDERS_KEY,
    STORE_STATUS_KEY,
    ...LABOR_RATE_KEYS,
]);

type LocationView = (typeof LOCATION_VIEWS)[number];

/**
 * Consolidate IS values from the three locations into consolidated for one month.
 * Sums inputs/subtotals (except avg ticket, store status, metrics), then computes
 * consolidated metrics from consolidated totals. Never re-evaluates GPS from orders×ticket.
 */
export function consolidateIsFromLocations(
    schema: SchemaEntry[],
    inputs: CaseInputs,
    getLocationValue: (view: LocationView, key: string) => number | string | null,
    getConsolidatedValue: (key: string) => number | string | null,
    setConsolidated: (key: string, value: number | string | null) => void,
): void {
    const isEntries = schema.filter((entry) => entry.tab === 'IS');

    for (const entry of isEntries) {
        const isLaborInput = entry.row == null && entry.kind === 'input' && LABOR_INPUT_KEYS.has(entry.key);
        if (
            (entry.row == null && !isLaborInput) ||
            entry.kind === 'text' ||
            entry.kind === 'metric'
        ) {
            continue;
        }
        if (inputs.consolidated_only.includes(entry.key)) {
            continue;
        }
        if (entry.kind !== 'input' && entry.kind !== 'subtotal') {
            continue;
        }
        if (EXCLUDED_FROM_SUM.has(entry.key)) {
            continue;
        }

        const memberGroup = ignoreGroupForLine(schema, entry.key);
        if (memberGroup && isGroupIgnoredAtAnyLocation(inputs, memberGroup)) {
            setConsolidated(entry.key, null);
            continue;
        }

        let sum = 0;
        let has = false;
        for (const view of LOCATION_VIEWS) {
            const raw = getLocationValue(view, entry.key);
            if (typeof raw === 'number') {
                sum += raw;
                has = true;
            }
        }
        setConsolidated(entry.key, has ? sum : null);
    }

    function weightedRate(numeratorKey: string, weightKey: string): number | null {
        let numerator = 0;
        let weight = 0;
        let has = false;
        for (const view of LOCATION_VIEWS) {
            const rate = getLocationValue(view, numeratorKey);
            const w = getLocationValue(view, weightKey);
            if (typeof rate === 'number' && typeof w === 'number' && w !== 0) {
                numerator += rate * w;
                weight += w;
                has = true;
            }
        }
        return has && weight !== 0 ? numerator / weight : null;
    }

    for (const rateKey of LABOR_RATE_KEYS) {
        const weightKey =
            rateKey === 'is.avg_salary_annual' ? 'is.salaried_headcount' : rateKey.replace('avg_hourly_rate', 'hourly_fte');
        const value = weightedRate(rateKey, weightKey);
        setConsolidated(rateKey, value);
    }

    const valuesByKey: Record<string, number | null> = {};
    for (const entry of isEntries) {
        if (entry.kind === 'text') {
            continue;
        }
        const raw = getConsolidatedValue(entry.key);
        valuesByKey[entry.key] = typeof raw === 'number' ? raw : null;
    }

    const gps = valuesByKey[GPS_KEY];
    const orders = valuesByKey[ORDERS_KEY];
    if (gps != null && orders != null && orders !== 0) {
        const ticket = gps / orders;
        setConsolidated(AVG_TICKET_KEY, ticket);
        valuesByKey[AVG_TICKET_KEY] = ticket;
    }

    const daysOpen = valuesByKey['is.days_open_for_business'];
    if (daysOpen != null && daysOpen !== 0 && orders != null) {
        const avgOrders = orders / daysOpen;
        setConsolidated(AVG_ORDERS_KEY, avgOrders);
        valuesByKey[AVG_ORDERS_KEY] = avgOrders;
    } else {
        setConsolidated(AVG_ORDERS_KEY, null);
        valuesByKey[AVG_ORDERS_KEY] = null;
    }

    for (const entry of isEntries) {
        if (entry.formula && entry.kind === 'metric' && entry.row != null) {
            const value = evalFormula(entry.formula, valuesByKey);
            setConsolidated(entry.key, value);
            valuesByKey[entry.key] = value;
        }
    }

    for (const entry of isEntries) {
        if (
            entry.formula &&
            entry.kind === 'subtotal' &&
            entry.row != null &&
            entry.key !== GPS_KEY &&
            entry.key !== ORDERS_KEY
        ) {
            const group = ignoreGroupForParent(schema, entry.key);
            if (group && isGroupIgnoredAtAnyLocation(inputs, group)) {
                continue;
            }
            const value = evalFormula(entry.formula, valuesByKey);
            setConsolidated(entry.key, value);
            valuesByKey[entry.key] = value;
        }
    }
}

export function consolidateHistoryMonth(
    values: Record<View, Record<string, (number | string | null)[]>>,
    schema: SchemaEntry[],
    inputs: CaseInputs,
    histIdx: number,
    nHistory: number,
): void {
    consolidateIsFromLocations(
        schema,
        inputs,
        (view, key) => values[view][key]?.[histIdx] ?? null,
        (key) => values.consolidated[key]?.[histIdx] ?? null,
        (key, value) => {
            if (!values.consolidated[key]) {
                values.consolidated[key] = new Array(nHistory).fill(null);
            }
            values.consolidated[key]![histIdx] = value;
        },
    );
}
